import parseTariff from '../personas/parseTariff'
import { DATE_KEY_PATTERN } from './formatDateKey'

/**
 * Het tarief dat op één dag gold voor één deelnemer.
 *
 * Het tarief hoort bij de deelnemer, met een ingangsdatum (besluit Gijs,
 * 2026-10-28). Heeft de deelnemer op die dag nog geen eigen tarief, dan geldt
 * het tarief van het traject — met `source: 'route'`, zodat het scherm dat kan
 * laten zien in plaats van het stil te gebruiken.
 *
 * AI-CONTEXT: See docs/pitfalls.md#database-coarse-sort-key-ties-break-arbitrarily
 * AI-CONTEXT: See docs/pitfalls.md#state-or-fallback-erases-zero
 */

/**
 * @typedef {object} PersonaTariff
 * @property {string} ID
 * @property {number|''} Amount - Bedrag in euro; `''` = niets ingevuld
 * @property {string} StartDate - Ingangsdatum als YYYY-MM-DD
 * @property {number|{toMillis: () => number}} [Created] - Aanmaakmoment
 * @property {boolean} [Deleted]
 */

/**
 * @typedef {object} TariffForDate
 * @property {number} amount - Bedrag in euro
 * @property {'persona'|'route'} source - Van de deelnemer of van het traject
 * @property {string} [startDate] - Ingangsdatum van het tarief van de deelnemer (YYYY-MM-DD); ontbreekt bij het traject
 */


/** @param {PersonaTariff['Created']} created @returns {number} */
const createdMillis = (created) => {
    if (typeof created === 'number') return created
    if (created && typeof created.toMillis === 'function') return created.toMillis()
    return 0
}

/**
 * Nieuwste ingangsdatum eerst. Bij een gelijke datum wint de laatst aangemaakte,
 * en daaronder het ID, zodat de volgorde niet afhangt van hoe Firestore de
 * documenten aanlevert.
 *
 * @param {PersonaTariff} a
 * @param {PersonaTariff} b
 * @returns {number}
 */
const compareTariffs = (a, b) => {
    if (a.StartDate !== b.StartDate) return a.StartDate < b.StartDate ? 1 : -1
    const created = createdMillis(b.Created) - createdMillis(a.Created)
    if (created !== 0) return created
    if (a.ID === b.ID) return 0
    return a.ID < b.ID ? 1 : -1
}

/** @param {PersonaTariff} tariff @returns {boolean} */
const isUsable = (tariff) =>
    !!tariff &&
    tariff.Deleted !== true &&
    typeof tariff.StartDate === 'string' &&
    DATE_KEY_PATTERN.test(tariff.StartDate) &&
    parseTariff(tariff.Amount) !== ''

/**
 * @param {Array<PersonaTariff>} tariffs - Tarieven van één deelnemer
 * @param {string} dateKey - De dag als YYYY-MM-DD
 * @param {*} routeTariff - `PersonaRoutes.Tariff` van het traject van de deelnemer
 * @returns {TariffForDate|null} `null` als er op die dag geen tarief is
 */
const tariffForDate = (tariffs, dateKey, routeTariff) => {
    const current = (tariffs || [])
        .filter(isUsable)
        .filter((tariff) => tariff.StartDate <= dateKey)
        .sort(compareTariffs)[0]

    if (current) return { amount: parseTariff(current.Amount), source: 'persona', startDate: current.StartDate }

    const fromRoute = parseTariff(routeTariff)
    if (fromRoute !== '') return { amount: fromRoute, source: 'route' }

    return null
}

export default tariffForDate
export { compareTariffs }
